import * as THREE from 'three';
import { MorpheusMap } from '../map/MorpheusMap';
import { WeaponSystem, WeaponType } from '../weapons/WeaponSystem';
import { fpsAudio } from '../audio/FPSAudio';

export interface WeaponInventoryItem {
  type: WeaponType;
  name: string;
  ammo: number;
  maxAmmo: number;
  hasWeapon: boolean;
  color: number;
}

export class FPSController {
  public camera: THREE.PerspectiveCamera;
  public id: string = 'player';
  public name: string = 'Player';
  public isLocked: boolean = false;

  public velocity: THREE.Vector3 = new THREE.Vector3();
  public onGround: boolean = false;
  public health: number = 100;
  public armor: number = 0;
  public isAlive: boolean = true;
  public respawnTimer: number = 0;
  public score: number = 0;
  public deaths: number = 0;
  public killerName: string = '';
  public deathReason: string = '';

  // Weapons & Inventory
  public weapons: Record<WeaponType, WeaponInventoryItem> = {
    enforcer: { type: 'enforcer', name: 'Enforcer', ammo: 50, maxAmmo: 199, hasWeapon: true, color: 0xffff00 },
    shock: { type: 'shock', name: 'Shock Rifle', ammo: 20, maxAmmo: 60, hasWeapon: false, color: 0x00fff2 },
    pulse: { type: 'pulse', name: 'Pulse Gun', ammo: 60, maxAmmo: 199, hasWeapon: false, color: 0x39ff14 },
    flak: { type: 'flak', name: 'Flak Cannon', ammo: 15, maxAmmo: 50, hasWeapon: false, color: 0xffaa00 },
    rocket: { type: 'rocket', name: 'Rocket Launcher', ammo: 12, maxAmmo: 48, hasWeapon: false, color: 0xff3300 },
    sniper: { type: 'sniper', name: 'Sniper Rifle', ammo: 10, maxAmmo: 40, hasWeapon: false, color: 0xffffff },
  };
  public currentWeapon: WeaponType = 'enforcer';

  // Input states
  private keys: Record<string, boolean> = {};
  private pitch: number = 0;
  private yaw: number = 0;
  private fireTimer: number = 0;
  private jumpCooldown: number = 0;
  private isPrimaryFiring: boolean = false;
  private isAltFiring: boolean = false;

  // Viewmodel
  public gunPivot: THREE.Group = new THREE.Group();
  private weaponModels: Record<WeaponType, THREE.Group> = {} as any;
  private gunBobTimer: number = 0;

  // Callbacks
  public onPlayerDeath?: (reason: string, killerId?: string) => void;
  public onPickupCollected?: (type: string) => void;

  constructor(camera: THREE.PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.setupViewmodels();
    this.setupInputs(domElement);
  }

  private setupViewmodels() {
    this.camera.add(this.gunPivot);

    this.weaponModels.enforcer = this.buildEnforcerModel();
    this.weaponModels.shock = this.buildShockRifleModel();
    this.weaponModels.pulse = this.buildPulseGunModel();
    this.weaponModels.flak = this.buildFlakCannonModel();
    this.weaponModels.rocket = this.buildRocketLauncherModel();
    this.weaponModels.sniper = this.buildSniperRifleModel();

    for (const [wType, group] of Object.entries(this.weaponModels)) {
      this.gunPivot.add(group);
      group.visible = wType === this.currentWeapon;
    }
  }

  private buildEnforcerModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.24, -0.24, -0.55);

    const steelMat = new THREE.MeshStandardMaterial({ color: 0x5b6b82, metalness: 0.8, roughness: 0.25 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.5, roughness: 0.5 });
    const silverMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.9, roughness: 0.2 });

    // Heavy Slide
    const slide = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.14, 0.44), steelMat);
    group.add(slide);

    // Muzzle barrel
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.16, 12), silverMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.01, -0.28);
    group.add(barrel);

    // Angled Grip
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.24, 0.14), darkMat);
    grip.position.set(0, -0.16, 0.08);
    grip.rotation.x = 0.22;
    group.add(grip);

    // Laser Sight on top
    const sight = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.06), new THREE.MeshBasicMaterial({ color: 0xffff00 }));
    sight.position.set(0, 0.085, 0.18);
    group.add(sight);

    const frontSight = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.03, 0.04), new THREE.MeshBasicMaterial({ color: 0x39ff14 }));
    frontSight.position.set(0, 0.085, -0.2);
    group.add(frontSight);

    return group;
  }

  private buildShockRifleModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.24, -0.23, -0.65);

    const violetMat = new THREE.MeshStandardMaterial({ color: 0x3b1d60, metalness: 0.7, roughness: 0.3 });
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xc4d1e0, metalness: 0.95, roughness: 0.15 });
    const cyanGlowMat = new THREE.MeshBasicMaterial({ color: 0x00fff2 });

    // Sleek chassis
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.13, 0.68), violetMat);
    group.add(body);

    // Central Plasma Core Coil (Glass chamber)
    const glass = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.055, 0.22, 12),
      new THREE.MeshPhysicalMaterial({ color: 0x00fff2, transmission: 0.8, transparent: true, opacity: 0.7 })
    );
    glass.rotation.x = Math.PI / 2;
    glass.position.set(0, 0.02, -0.05);
    group.add(glass);

    // Inner pulsating shock core
    const core = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.24, 8), cyanGlowMat);
    core.rotation.x = Math.PI / 2;
    core.position.set(0, 0.02, -0.05);
    group.add(core);

    // Twin Emitter Prongs (Classic ASMD Shock Rifle front forks)
    [-0.065, 0.065].forEach((x) => {
      const prong = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.03, 0.28), chromeMat);
      prong.position.set(x, 0.01, -0.44);
      group.add(prong);

      const tip = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.035, 0.07), cyanGlowMat);
      tip.position.set(x, 0.01, -0.58);
      group.add(tip);
    });

    // Stock
    const stock = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.18, 0.24), violetMat);
    stock.position.set(0, -0.06, 0.4);
    group.add(stock);

    return group;
  }

  private buildPulseGunModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.25, -0.24, -0.62);

    const slateMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.7, roughness: 0.35 });
    const silverMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.9, roughness: 0.2 });
    const greenGlowMat = new THREE.MeshBasicMaterial({ color: 0x39ff14 });

    // Main receiver
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.18, 0.54), slateMat);
    group.add(body);

    // Top carrying handle
    const handle = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.1, 0.32), slateMat);
    handle.position.set(0, 0.13, -0.04);
    group.add(handle);

    // Glowing green plasma battery pack
    const battery = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.22, 12), greenGlowMat);
    battery.rotation.x = Math.PI / 2;
    battery.position.set(0, -0.1, 0.05);
    group.add(battery);

    // Tri-Barrel Rotating Cluster
    const barrelGeo = new THREE.CylinderGeometry(0.024, 0.024, 0.32, 10);
    const b1 = new THREE.Mesh(barrelGeo, silverMat);
    b1.rotation.x = Math.PI / 2;
    b1.position.set(0, 0.045, -0.4);
    group.add(b1);

    const b2 = new THREE.Mesh(barrelGeo, silverMat);
    b2.rotation.x = Math.PI / 2;
    b2.position.set(-0.045, -0.03, -0.4);
    group.add(b2);

    const b3 = new THREE.Mesh(barrelGeo, silverMat);
    b3.rotation.x = Math.PI / 2;
    b3.position.set(0.045, -0.03, -0.4);
    group.add(b3);

    // Muzzle ring
    const mRing = new THREE.Mesh(new THREE.TorusGeometry(0.055, 0.012, 8, 16), greenGlowMat);
    mRing.position.set(0, 0, -0.54);
    group.add(mRing);

    return group;
  }

  private buildFlakCannonModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.25, -0.24, -0.6);

    // Iconic UT Gold/Yellow Armour
    const yellowMat = new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.55, roughness: 0.35 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x1f2937, metalness: 0.8, roughness: 0.4 });
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, metalness: 0.95, roughness: 0.15 });

    // Chunky Heavy Body
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.55), yellowMat);
    group.add(body);

    // The iconic "Smiling" Wide Flak Muzzle Shroud
    const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.18), yellowMat);
    muzzle.position.set(0, 0.02, -0.36);
    group.add(muzzle);

    // Wide horizontal mouth opening
    const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.07, 0.06), new THREE.MeshBasicMaterial({ color: 0x0a0a0a }));
    mouth.position.set(0, 0.02, -0.45);
    group.add(mouth);

    // Top shrapnel ammo canister box (slanted on top right)
    const can = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.12, 0.24), darkMat);
    can.position.set(0.04, 0.17, -0.04);
    can.rotation.z = -0.12;
    group.add(can);

    // Side Hydraulic Cocking Piston
    const piston = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.42, 12), chromeMat);
    piston.rotation.x = Math.PI / 2;
    piston.position.set(-0.14, 0.04, -0.1);
    group.add(piston);

    // Hazard orange trim
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.28), new THREE.MeshBasicMaterial({ color: 0xff6600 }));
    stripe.position.set(0.115, 0.02, 0.05);
    group.add(stripe);

    return group;
  }

  private buildRocketLauncherModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.26, -0.26, -0.64);

    const redMat = new THREE.MeshStandardMaterial({ color: 0x991b1b, metalness: 0.65, roughness: 0.35 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x27272a, metalness: 0.8, roughness: 0.4 });
    const silverMat = new THREE.MeshStandardMaterial({ color: 0x71717a, metalness: 0.85, roughness: 0.25 });

    // Main heavy ordnance chassis
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.65), redMat);
    group.add(body);

    // Multi-Tube Revolving Drum
    const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.1, 16), darkMat);
    drum.rotation.x = Math.PI / 2;
    drum.position.set(0, 0.02, -0.32);
    group.add(drum);

    // 6 Clustered Rocket Launch Tubes
    const tubeGeo = new THREE.CylinderGeometry(0.028, 0.028, 0.34, 12);
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x = Math.cos(angle) * 0.07;
      const y = Math.sin(angle) * 0.07 + 0.02;
      const tube = new THREE.Mesh(tubeGeo, silverMat);
      tube.rotation.x = Math.PI / 2;
      tube.position.set(x, y, -0.48);
      group.add(tube);
    }

    // Side Target Lock Guidance Module
    const pod = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.22), darkMat);
    pod.position.set(-0.13, 0.12, -0.15);
    group.add(pod);

    const laser = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.02), new THREE.MeshBasicMaterial({ color: 0xff3300 }));
    laser.position.set(-0.13, 0.12, -0.27);
    group.add(laser);

    return group;
  }

  private buildSniperRifleModel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.22, -0.22, -0.7);

    const blackMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.75, roughness: 0.3 });
    const steelMat = new THREE.MeshStandardMaterial({ color: 0x64748b, metalness: 0.85, roughness: 0.25 });
    const lensMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8 });

    // Long sleek tactical receiver
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.14, 0.8), blackMat);
    group.add(body);

    // Extended Fluted Sniper Barrel
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.65, 12), steelMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.03, -0.68);
    group.add(barrel);

    // Muzzle Suppressor / Brake
    const muzzle = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.042, 0.14, 12), blackMat);
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.set(0, 0.03, -1.02);
    group.add(muzzle);

    // Large High-Magnification Scope
    const scopeBody = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.4, 16), blackMat);
    scopeBody.rotation.x = Math.PI / 2;
    scopeBody.position.set(0, 0.15, -0.12);
    group.add(scopeBody);

    const scopeBell = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.045, 0.08, 16), blackMat);
    scopeBell.rotation.x = Math.PI / 2;
    scopeBell.position.set(0, 0.15, -0.32);
    group.add(scopeBell);

    const frontLens = new THREE.Mesh(new THREE.CircleGeometry(0.055, 16), lensMat);
    frontLens.position.set(0, 0.15, -0.365);
    group.add(frontLens);

    // Scope Mount Risers
    [-0.22, -0.02].forEach((z) => {
      const riser = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.06, 0.04), steelMat);
      riser.position.set(0, 0.1, z);
      group.add(riser);
    });

    return group;
  }

  private setupInputs(domElement: HTMLElement) {
    window.addEventListener('keydown', (e) => {
      this.keys[e.code] = true;
      if (e.key) this.keys[e.key.toLowerCase()] = true;

      // Respawn on Space or Enter while dead
      if (!this.isAlive && (e.code === 'Space' || e.code === 'Enter' || e.key === ' ')) {
        this.triggerRespawn();
      }

      // Weapon slots 1 to 6
      if (e.code === 'Digit1' || e.key === '1') this.switchWeapon('enforcer');
      if (e.code === 'Digit2' || e.key === '2') this.switchWeapon('shock');
      if (e.code === 'Digit3' || e.key === '3') this.switchWeapon('pulse');
      if (e.code === 'Digit4' || e.key === '4') this.switchWeapon('flak');
      if (e.code === 'Digit5' || e.key === '5') this.switchWeapon('rocket');
      if (e.code === 'Digit6' || e.key === '6') this.switchWeapon('sniper');
    });

    window.addEventListener('keyup', (e) => {
      this.keys[e.code] = false;
      if (e.key) this.keys[e.key.toLowerCase()] = false;
    });

    const handlePointerAndRespawn = () => {
      if (!this.isAlive) {
        this.triggerRespawn();
      }
      if (!this.isLocked) {
        domElement.requestPointerLock();
      }
    };

    domElement.addEventListener('click', handlePointerAndRespawn);
    window.addEventListener('click', () => {
      if (!this.isAlive || !this.isLocked) {
        handlePointerAndRespawn();
      }
    });

    document.addEventListener('pointerlockchange', () => {
      this.isLocked = document.pointerLockElement === domElement;
    });

    domElement.addEventListener('mousemove', (e) => {
      if (!this.isLocked || !this.isAlive) return;

      const sensitivity = 0.0022;
      this.yaw -= e.movementX * sensitivity;
      this.pitch -= e.movementY * sensitivity;

      // Clamp pitch to [-85 deg, +85 deg]
      const maxPitch = (Math.PI / 2) * 0.95;
      this.pitch = Math.max(-maxPitch, Math.min(maxPitch, this.pitch));

      this.updateCameraRotation();
    });

    domElement.addEventListener('mousedown', (e) => {
      if (!this.isAlive) {
        handlePointerAndRespawn();
        return;
      }
      if (!this.isLocked) {
        handlePointerAndRespawn();
        return;
      }
      if (e.button === 0) this.isPrimaryFiring = true;
      if (e.button === 2) this.isAltFiring = true;
    });

    window.addEventListener('mousedown', () => {
      if (!this.isAlive) {
        handlePointerAndRespawn();
      }
    });

    domElement.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.isPrimaryFiring = false;
      if (e.button === 2) this.isAltFiring = false;
    });

    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.isPrimaryFiring = false;
      if (e.button === 2) this.isAltFiring = false;
    });

    // Prevent context menu on right click
    domElement.addEventListener('contextmenu', (e) => e.preventDefault());

    // Weapon cycle with mouse wheel
    domElement.addEventListener('wheel', (e) => {
      if (!this.isLocked || !this.isAlive) return;
      const weaponTypes: WeaponType[] = ['enforcer', 'shock', 'pulse', 'flak', 'rocket', 'sniper'];
      const owned = weaponTypes.filter((w) => this.weapons[w].hasWeapon);
      const currIdx = owned.indexOf(this.currentWeapon);

      if (e.deltaY > 0) {
        const nextIdx = (currIdx + 1) % owned.length;
        this.switchWeapon(owned[nextIdx]);
      } else {
        const prevIdx = (currIdx - 1 + owned.length) % owned.length;
        this.switchWeapon(owned[prevIdx]);
      }
    });
  }

  public switchWeapon(type: WeaponType) {
    if (this.weapons[type].hasWeapon) {
      this.currentWeapon = type;
      for (const [wType, group] of Object.entries(this.weaponModels)) {
        group.visible = wType === type;
      }
      // Gun switch quick drop/raise animation
      this.gunPivot.position.y = -0.15;
    }
  }

  private updateCameraRotation() {
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.camera.rotation.z = 0;
  }

  public takeDamage(amount: number, attackerId: string): { killed: boolean; wasHeadshot: boolean } {
    if (!this.isAlive) return { killed: false, wasHeadshot: false };

    fpsAudio.playSound('hit');

    // Shield/Armor absorbs 70% of damage
    if (this.armor > 0) {
      const absorbed = Math.min(this.armor, amount * 0.7);
      this.armor -= absorbed;
      amount -= absorbed;
    }

    this.health -= amount;

    if (this.health <= 0) {
      this.health = 0;
      this.isAlive = false;
      this.deaths++;
      this.respawnTimer = 3.0;
      this.gunPivot.visible = false;
      fpsAudio.playSound('death');
      if (this.onPlayerDeath) this.onPlayerDeath('fragged', attackerId);
      return { killed: true, wasHeadshot: false };
    }

    return { killed: false, wasHeadshot: false };
  }

  public respawn(spawnPos: THREE.Vector3) {
    this.camera.position.set(spawnPos.x, spawnPos.y + 1.8, spawnPos.z);
    this.velocity.set(0, 0, 0);
    this.health = 100;
    this.armor = 0;
    this.isAlive = true;
    this.onGround = true;
    this.isPrimaryFiring = false;
    this.isAltFiring = false;
    this.fireTimer = 0;
    this.jumpCooldown = 0;
    this.keys = {};
    this.respawnTimer = 0;
    this.gunPivot.position.set(0, 0, 0);
    this.gunPivot.visible = true;
    this.camera.rotation.z = 0;
    this.switchWeapon('enforcer');
    if (this.weapons.enforcer.ammo <= 0) {
      this.weapons.enforcer.ammo = 50;
    }
  }

  public triggerRespawn() {
    if (!this.isAlive && this.respawnTimer <= 2.8) {
      this.respawnTimer = 0;
    }
  }

  public update(deltaTime: number, map: MorpheusMap, weaponSystem: WeaponSystem) {
    if (!this.isAlive) {
      this.respawnTimer -= deltaTime;

      // Death tumble animation: camera drops and tilts to the deck
      this.camera.rotation.z = THREE.MathUtils.lerp(this.camera.rotation.z, 0.58, 0.08);
      if (this.camera.position.y > -20) {
        this.camera.position.y -= 2.5 * deltaTime;
      }

      // Fast respawn on click, space, or enter
      const isInputRespawn = this.isPrimaryFiring || this.keys['Space'] || this.keys[' '] || this.keys['Enter'];
      if (isInputRespawn && this.respawnTimer <= 2.8) {
        this.respawnTimer = 0;
      }

      if (this.respawnTimer <= 0) {
        const spawn = map.spawnPoints[Math.floor(Math.random() * map.spawnPoints.length)];
        this.respawn(spawn);
      }
      return;
    }

    // Death abyss check
    if (this.camera.position.y < -25) {
      this.takeDamage(999, 'world');
      return;
    }

    this.jumpCooldown = Math.max(0, this.jumpCooldown - deltaTime);
    this.fireTimer = Math.max(0, this.fireTimer - deltaTime);

    // Weapon firing
    if ((this.isPrimaryFiring || this.isAltFiring) && this.fireTimer <= 0) {
      this.handleFire(weaponSystem, this.isAltFiring);
    }

    // Viewmodel smoothing & bobbing
    this.gunPivot.position.y = THREE.MathUtils.lerp(this.gunPivot.position.y, 0, 0.15);

    // Jump pad detection
    const playerFeet = this.camera.position.clone().add(new THREE.Vector3(0, -1.7, 0));
    for (const pad of map.jumpPads) {
      const dist2d = new THREE.Vector2(playerFeet.x - pad.position.x, playerFeet.z - pad.position.z).length();
      if (dist2d < pad.radius && Math.abs(playerFeet.y - pad.position.y) < 2.2 && this.jumpCooldown <= 0) {
        this.velocity.copy(pad.targetVelocity);
        this.jumpCooldown = 1.2;
        this.onGround = false;
        fpsAudio.playSound('jumppad');
        break;
      }
    }

    // Movement input vector relative to camera yaw
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));

    const moveInput = new THREE.Vector3();
    const isW = this.keys['KeyW'] || this.keys['w'] || this.keys['ArrowUp'];
    const isS = this.keys['KeyS'] || this.keys['s'] || this.keys['ArrowDown'];
    const isD = this.keys['KeyD'] || this.keys['d'] || this.keys['ArrowRight'];
    const isA = this.keys['KeyA'] || this.keys['a'] || this.keys['ArrowLeft'];

    if (isW) moveInput.add(forward);
    if (isS) moveInput.sub(forward);
    if (isD) moveInput.add(right);
    if (isA) moveInput.sub(right);

    const isMoving = moveInput.lengthSq() > 0.001;
    if (isMoving) moveInput.normalize();

    // Speed constants (UT style fast strafing and floaty jumps)
    const runSpeed = 15.0;
    const accel = this.onGround ? 0.22 : 0.05;

    this.velocity.x = THREE.MathUtils.lerp(this.velocity.x, moveInput.x * runSpeed, accel);
    this.velocity.z = THREE.MathUtils.lerp(this.velocity.z, moveInput.z * runSpeed, accel);

    // Jump in low-g
    const isJump = this.keys['Space'] || this.keys[' '];
    if (isJump && this.onGround && this.jumpCooldown <= 0) {
      this.velocity.y = 9.8; // High soar jump in low-g
      this.onGround = false;
      this.jumpCooldown = 0.3;
    }

    // Low gravity (-10.0 m/s²)
    this.velocity.y -= 10.0 * deltaTime;

    // Gun bobbing when moving on ground
    if (this.onGround && isMoving) {
      this.gunBobTimer += deltaTime * 12;
      this.gunPivot.position.x = Math.sin(this.gunBobTimer) * 0.02;
      this.gunPivot.position.y = Math.abs(Math.cos(this.gunBobTimer)) * 0.018;
    }

    // Move & Collide
    const nextPos = this.camera.position.clone().addScaledVector(this.velocity, deltaTime);
    this.resolveMapCollisions(nextPos, map);

    // Pickups collection
    for (const pickup of map.pickups) {
      if (!pickup.active) continue;
      if (this.camera.position.distanceTo(pickup.position) < 2.4) {
        pickup.active = false;
        pickup.mesh.visible = false;
        pickup.timer = pickup.respawnTime;
        fpsAudio.playSound('pickup');

        if (pickup.type === 'health_pack') {
          this.health = Math.min(100, this.health + 25);
          if (this.onPickupCollected) this.onPickupCollected('+25 Health');
        } else if (pickup.type === 'health_vial') {
          this.health = Math.min(199, this.health + 5);
          if (this.onPickupCollected) this.onPickupCollected('+5 Health Vial');
        } else if (pickup.type === 'armor') {
          this.armor = Math.min(100, this.armor + 50);
          if (this.onPickupCollected) this.onPickupCollected('+50 Body Armor');
        } else if (pickup.type === 'shield_belt') {
          this.armor = Math.min(150, this.armor + 100);
          if (this.onPickupCollected) this.onPickupCollected('Shield Belt (100 Shield)');
        } else if (pickup.type.startsWith('weapon_')) {
          const wType = pickup.type.replace('weapon_', '') as WeaponType;
          this.weapons[wType].hasWeapon = true;
          this.weapons[wType].ammo = Math.min(
            this.weapons[wType].maxAmmo,
            this.weapons[wType].ammo + (wType === 'shock' ? 20 : wType === 'flak' ? 15 : 12)
          );
          this.switchWeapon(wType);
          if (this.onPickupCollected) this.onPickupCollected(`Picked up ${this.weapons[wType].name}`);
        }
      }
    }
  }

  private handleFire(weaponSystem: WeaponSystem, isAlt: boolean) {
    const cur = this.weapons[this.currentWeapon];
    if (cur.ammo <= 0) return;

    cur.ammo--;

    // Firing cooldowns
    const cooldownMap: Record<WeaponType, number> = {
      enforcer: 0.35,
      shock: isAlt ? 0.65 : 0.75,
      pulse: 0.11,
      flak: 0.8,
      rocket: 1.0,
      sniper: 1.2,
    };
    this.fireTimer = cooldownMap[this.currentWeapon] || 0.4;

    // Viewmodel recoil punch
    this.gunPivot.position.z += 0.08;
    this.gunPivot.position.y += 0.03;

    // Camera ray direction
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion).normalize();
    const origin = this.camera.position.clone().add(dir.clone().multiplyScalar(0.5));

    weaponSystem.fire(this.currentWeapon, this.id, origin, dir, isAlt);
  }

  private resolveMapCollisions(nextPos: THREE.Vector3, map: MorpheusMap) {
    const playerRadius = 0.5;
    const playerHeight = 1.8;
    const stepHeight = 0.45;
    this.onGround = false;

    // 1. Vertical sweep (gravity landing & ceiling)
    const feetY = nextPos.y - playerHeight;
    const playerBoxY = new THREE.Box3(
      new THREE.Vector3(this.camera.position.x - playerRadius, feetY, this.camera.position.z - playerRadius),
      new THREE.Vector3(this.camera.position.x + playerRadius, nextPos.y, this.camera.position.z + playerRadius)
    );

    for (const col of map.colliders) {
      if (playerBoxY.intersectsBox(col.box)) {
        if (this.velocity.y <= 0 && (this.camera.position.y - playerHeight >= col.box.max.y - 0.8 || feetY <= col.box.max.y)) {
          nextPos.y = col.box.max.y + playerHeight;
          this.velocity.y = 0;
          this.onGround = true;
        } else if (this.velocity.y > 0 && this.camera.position.y <= col.box.min.y + 0.4) {
          nextPos.y = col.box.min.y;
          this.velocity.y = 0;
        }
      }
    }

    // 2. Horizontal X sweep (elevated above stepHeight so floor never intercepts horizontal motion)
    const currentFeetY = nextPos.y - playerHeight;
    const bodyBottom = currentFeetY + stepHeight;
    const bodyTop = nextPos.y - 0.05;

    const playerBoxX = new THREE.Box3(
      new THREE.Vector3(nextPos.x - playerRadius, bodyBottom, this.camera.position.z - playerRadius),
      new THREE.Vector3(nextPos.x + playerRadius, bodyTop, this.camera.position.z + playerRadius)
    );
    for (const col of map.colliders) {
      if (col.type === 'floor' && col.box.max.y <= currentFeetY + stepHeight + 0.1) continue;
      if (playerBoxX.intersectsBox(col.box)) {
        nextPos.x = this.camera.position.x;
        this.velocity.x = 0;
        break;
      }
    }

    // 3. Horizontal Z sweep
    const playerBoxZ = new THREE.Box3(
      new THREE.Vector3(nextPos.x - playerRadius, bodyBottom, nextPos.z - playerRadius),
      new THREE.Vector3(nextPos.x + playerRadius, bodyTop, nextPos.z + playerRadius)
    );
    for (const col of map.colliders) {
      if (col.type === 'floor' && col.box.max.y <= currentFeetY + stepHeight + 0.1) continue;
      if (playerBoxZ.intersectsBox(col.box)) {
        nextPos.z = this.camera.position.z;
        this.velocity.z = 0;
        break;
      }
    }

    this.camera.position.copy(nextPos);
  }
}
